/**
 * ISO timestamp generation, pure millisecond arithmetic, AND the parse-backed conversions -- the whole of ISO-timestamp
 * handling in one leaf.
 *
 * The conversions used to sit apart in `tools/dates/iso-arithmetic.ts` because they needed `parseDate`, which lived in
 * `utils/dates/parsing.ts`: composing two `utils/` leaves is banned outright (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`,
 * no allowlist), so `tools/` was the only layer that could hold them. `parseDate` moved to `const/date-parse-format.ts`
 * on 2026-08-20, which this leaf may value-import, and the split had nothing left to express.
 */

import { epochMsToISODate } from '@federa/const/epoch-date';
import { parseDate } from '@federa/const/date-parse-format';
import type { DateInput } from '@federa/types/formatters-date-input';

export function nowISO(): string {
	return new Date().toISOString();
}

/**
 * Today's UTC calendar day, YYYY-MM-DD. Derived from the `epochMsToISODate` const SSOT rather than a second inlined
 * `nowISO().slice(0, 10)` -- one bucketing body, so this accessor cannot drift from the canonical one.
 *
 * UTC, DELIBERATELY. Callers wanting the day an OPERATOR, a buyer, or a document reads want the EASTERN business day
 * and take `todayInTimeZone` (`const/zoned-day.ts`) instead: from 20:00 Eastern onward the two answers differ.
 */
export function todayUTCDate(): string {
	return epochMsToISODate(Date.now());
}

/** ISO timestamp `ms` in the past; a negative `ms` moves into the future. */
export function subtractMsISO(ms: number): string {
	return new Date(Date.now() - ms).toISOString();
}

/** Accepts ms-since-epoch, ISO string or Date; returns '' when invalid. */
export function toISO(timestamp: DateInput): string {
	const date = parseDate(timestamp);
	return date ? date.toISOString() : '';
}

/** Falsy input yields the current time; a string input passes through UNPARSED (so '2024-01-15' stays '2024-01-15', not a full ISO timestamp). */
export function formatDateTimeISO(value: DateInput): string {
	if (!value) return nowISO();
	if (typeof value === 'string') return value;
	return toISO(value) || nowISO();
}
